import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { SelectOption, SelectOptionProps } from '../SelectOption'
import { MultipleValue } from '../types'

export const useSelectMultiple = ({
  defaultValue,
  value,
  onChange,
  children,
}: {
  defaultValue?: MultipleValue
  value?: MultipleValue
  onChange?: (value: MultipleValue) => void
  children: ReactNode
}) => {
  const isControlled = value !== undefined

  const { options, optionIds } = useMemo(() => {
    const optionsArray: React.ReactElement<SelectOptionProps>[] = []
    const idsArray: string[] = []
    React.Children.forEach(children, (child) => {
      if (React.isValidElement<SelectOptionProps>(child) && child.type === SelectOption) {
        optionsArray.push(child)
        idsArray.push(child.props.id)
      }
    })
    return { options: optionsArray, optionIds: idsArray }
  }, [children])

  const [selectedIndexes, setSelectedIndexes] = useState<number[]>(() => {
    if (Array.isArray(defaultValue)) {
      return defaultValue
        .map((value) => optionIds.findIndex((id) => id === value))
        .filter((index) => index !== -1)
    }
    return []
  })

  // Update selected indexes when value changes
  useEffect(() => {
    if (!isControlled || !Array.isArray(value)) return
    const newIndexes = value.map((val) => optionIds.indexOf(val)).filter((index) => index !== -1)
    setSelectedIndexes(newIndexes)
  }, [value, optionIds, setSelectedIndexes, isControlled])

  const selectedChildren = useMemo(
    () =>
      selectedIndexes
        .map((index) => {
          const optionsProps = options[index]?.props
          if (!optionsProps) return null
          return {
            children: optionsProps.children,
            icon: optionsProps.icon,
            index,
          }
        })
        .filter((item): item is NonNullable<typeof item> => item !== null),
    [selectedIndexes, options],
  )

  const handleSelect = useCallback(
    (index: number) => {
      const newIndexes = [...selectedIndexes]
      const selectedIndex = newIndexes.indexOf(index)

      if (selectedIndex !== -1) {
        newIndexes.splice(selectedIndex, 1)
      } else {
        newIndexes.push(index)
      }

      if (!isControlled) {
        setSelectedIndexes(newIndexes)
      }
      onChange?.(newIndexes.map((index) => optionIds[index]))
    },
    [isControlled, onChange, optionIds, selectedIndexes],
  )

  return {
    selectedIndexes,
    handleSelect,
    selectedChildren,
  }
}
